<template>
    <div class="card mb-2">
        <div class="card-header pr-1 d-flex align-items-center">
            <span class="btnlike mr-auto">
                <h4 class="mb-0">{{ title }}</h4>
            </span>
        </div>
        <div class="card-body">
            <p class="text-muted">
                Status:
                <span v-if="isSynced">Synced on {{ syncedOn }}</span>
                <span v-else>Not yet synced</span>
            </p>
            <button class="btn btn-outline-primary btn-block mb-2" :disabled="pushing || terminating" @click.prevent="push">
                {{ buttonLabel }}
            </button>
            <button v-if="terminateEndpoint" class="btn btn-outline-danger btn-block mb-2" :disabled="pushing || terminating" @click.prevent="terminate">
                {{ terminating ? 'Terminating…' : `Terminate in ${title}` }}
            </button>
            <div v-if="terminateResult" class="mt-2 small">
                <div v-for="(w, i) in terminateResult.warnings" :key="'w' + i" class="text-warning">⚠ {{ w }}</div>
                <div v-for="(e, i) in terminateResult.errors" :key="'e' + i" class="text-danger">✗ {{ e }}</div>
            </div>

            <hr v-if="updates.length">
            <div v-if="updates.length">
                <ul class="list-unstyled m-0 text-small">
                    <li class="d-none d-sm-block">
                        <div class="form-row align-items-baseline py-hf">
                            <div class="col-5"><h5 class="theading m-0">Date &amp; Time</h5></div>
                            <div class="col-4"><h5 class="theading m-0">Action</h5></div>
                            <div class="col-3"><h5 class="theading m-0">Status</h5></div>
                        </div>
                    </li>
                    <li class="boxed-li-bottom" v-for="updateObj in updates" :key="updateObj.id">
                        <div class="form-row align-items-baseline py-hf">
                            <div class="col-12 col-sm-5"><span>{{ $filters.datetimeshort(updateObj.created_on) }}</span></div>
                            <div class="col-12 col-sm-4"><span>{{ updateObj.action_pretty }}</span></div>
                            <div class="col-12 col-sm-3">
                                <a href @click.prevent="$emit('show-status', updateObj)">{{ updateObj.status_pretty }}</a>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { escapeHtml, findPushResult, nextPollDelay, posUpdateErrorMessage, pushButtonLabel, pushEndpoint, terminateResultFromUpdate, terminateSummary } from './pos-push-card-helpers'

// Pushes and terminates run in Celery tasks, so poll the sync log for the row the task writes.
const POLL_INTERVAL_MS = 3000
const PUSH_POLL_MAX_ATTEMPTS = 20
const TERMINATE_POLL_MAX_ATTEMPTS = 60  // about 3 minutes
// then a terminate keeps checking at this pace until its row lands
const SLOW_POLL_INTERVAL_MS = 15000

export default {
    props: {
        client: {type: Object, required: true},
        employee: {type: Object, required: true},
        posCode: {type: String, required: true},
        title: {type: String, required: true},
        posDataUpdates: {type: Array, default: () => []},
        // empty endpoint = no terminate button
        terminateEndpoint: {type: String, default: ''},
        terminateConfirm: {type: String, default: ''},
    },
    emits: ['show-status'],
    data() {
        return {
            pushing: false,
            terminating: false,
            terminateResult: null,
            pollTimer: null,
            // this POS's sync log as re-fetched after a push; null until then
            refreshed: null,
        }
    },
    computed: {
        updates() {
            if (this.refreshed) return this.refreshed.pos_data_updates
            return (this.posDataUpdates || []).filter(u => u.pos === this.posCode)
        },
        isSynced() {
            return this.refreshed ? this.refreshed.is_synced_to_pos : this.employee.is_synced_to_pos
        },
        syncedOn() {
            return this.refreshed ? this.refreshed.synced_to_pos_on : this.employee.synced_to_pos_on
        },
        buttonLabel() {
            return pushButtonLabel(this.title, this.isSynced, this.pushing)
        },
        employeeUrl() {
            return `/clients/${this.client.id}/employees/${this.employee.id}`
        },
    },
    watch: {
        'employee.id'() {
            this.stopPolling()
            this.pushing = false
            this.terminating = false
            this.terminateResult = null
            this.refreshed = null
        },
    },
    beforeUnmount() {
        this.stopPolling()
    },
    methods: {
        push() {
            this.stopPolling()  // a terminate may still be re-checking slowly
            this.pushing = true
            const seenIds = this.updates.map(u => u.id)
            this.$api.post(`${this.employeeUrl}/${pushEndpoint(this.posCode)}`, {pos: this.posCode}).then(() => {
                this.poll({
                    seenIds,
                    action: 'hire',
                    maxAttempts: PUSH_POLL_MAX_ATTEMPTS,
                    onResult: update => {
                        this.pushing = false
                        this.reportResult(update)
                    },
                    onTimeout: () => {
                        this.pushing = false
                        this.$bus.showWarning(`${this.title} push not finished yet`, 'The result will appear in the sync log once it completes.')
                    },
                    onError: errors => {
                        this.pushing = false
                        this.$bus.showError(errors.__all__ || [`Could not check the ${this.title} push. See the sync log.`])
                    },
                })
            }).catch(errors => {
                this.pushing = false
                this.$bus.showError(errors.__all__ || errors.pos || [`Push failed. Check the ${this.title} sync log.`])
            })
        },
        terminate() {
            if (!window.confirm(this.terminateConfirm)) return
            const seenIds = this.updates.map(u => u.id)
            this.stopPolling()
            this.terminating = true
            this.terminateResult = null
            const employeeId = this.employee.id
            this.$api.post(`${this.employeeUrl}/${this.terminateEndpoint}`, {}).then(() => {
                if (this.employee.id !== employeeId) return  // sidebar moved on
                this.poll({
                    seenIds,
                    action: 'terminate',
                    maxAttempts: TERMINATE_POLL_MAX_ATTEMPTS,
                    slowIntervalMs: SLOW_POLL_INTERVAL_MS,
                    onResult: update => {
                        this.terminating = false
                        const result = terminateResultFromUpdate(update)
                        this.terminateResult = result
                        const summary = terminateSummary(result)
                        const show = {error: this.$bus.showError, warning: this.$bus.showWarning, info: this.$bus.showInfo}[summary.level]
                        show.call(this.$bus, summary.title, summary.lines.join('\n'))
                    },
                    onTimeout: () => {
                        this.terminating = false
                        this.$bus.showWarning('CrunchTime terminate still running', 'This card will update when it finishes. You can also reopen this employee later to see the result in the history.')
                    },
                    onError: errors => {
                        this.terminating = false
                        this.$bus.showError((errors.__all__ || [`Could not check the ${this.title} terminate. See the history below.`]).map(escapeHtml))
                    },
                })
            }).catch(errors => {
                if (this.employee.id !== employeeId) return
                this.terminating = false
                this.$bus.showError((errors.__all__ || [`Terminate failed. Check the ${this.title} sync log.`]).map(escapeHtml))
            })
        },
        // job: {seenIds, action, maxAttempts, slowIntervalMs?, onResult(update), onTimeout(), onError(errors)}
        poll(job, attempt = 1, delay = POLL_INTERVAL_MS) {
            const timer = setTimeout(() => {
                const url = `${this.employeeUrl}/pos-data-updates?pos=${encodeURIComponent(this.posCode)}`
                // a background poll shouldn't extend the session
                this.$api.get(url, true).then(resp => {
                    if (this.pollTimer !== timer) return  // stopped or superseded while in flight
                    this.refreshed = resp
                    const update = findPushResult(resp.pos_data_updates, job.seenIds, job.action)
                    if (update) {
                        job.onResult(update)
                        return
                    }
                    const next = nextPollDelay(attempt, job.maxAttempts, POLL_INTERVAL_MS, job.slowIntervalMs)
                    if (next.timedOut) job.onTimeout()
                    if (next.delay) this.poll(job, attempt + 1, next.delay)
                }).catch(errors => {
                    if (this.pollTimer !== timer) return
                    job.onError(errors)
                })
            }, delay)
            this.pollTimer = timer
        },
        reportResult(update) {
            if (update.status === 'success') {
                this.$bus.showInfo(`Pushed to ${this.title}`, `The push to ${this.title} succeeded.`)
            } else {
                this.$bus.showError(`${this.title} push failed`, escapeHtml(posUpdateErrorMessage(update)) || 'See the sync log for details.')
            }
        },
        stopPolling() {
            clearTimeout(this.pollTimer)
            this.pollTimer = null
        },
    },
}
</script>
